/* eslint-disable max-classes-per-file */

import _ from 'underscore';

import { Registry } from './registry';
import {
  defineConstProperty,
  defineProducedProperty,
  defineProducedPropertyOnClass,
} from './reflection';
import type {
  CallableTypeOptions,
  CollectionTypeOptions,
  EdmCallableType,
  EdmCollectionType,
  EdmEntityType,
  EdmObjectType,
  EdmParameter,
  EdmPrimitiveType,
  EdmProperty,
  EdmType,
  EntityDataModel,
  EntityTypeOptions,
  NamespaceName,
  ObjectTypeOptions,
  OneOfTypeOptions,
  ParameterInfo,
  ParameterOptions,
  PrimitiveTypeOptions,
  PropertyInfo,
  PropertyOptions,
  TypeName,
  TypeOptions,
  TypeRegistry,
} from '../types';

const ONEOF_TYPE_PREFIX = '@ONEOF';
const COLLECTION_TYPE_POSTFIX = '@COLL';

function isOneOfType(typeName: TypeName) {
  return typeName.indexOf(ONEOF_TYPE_PREFIX) === 0;
}

function hasPostfix(str: string, postfix: string) {
  return str.slice(-postfix.length) === postfix;
}

function removePostfix(str: string, postfix: string) {
  return str.slice(0, -postfix.length);
}

/**
 * @param {EDM} edm - The EDM object to apply this plugin to
 * @return {void}
 */
export default function typesPlugin(edm: EntityDataModel) {
  if (edm.types) {
    return;
  }

  const oneOfTypes = new Map<TypeName, EdmType>();

  defineConstProperty(edm, 'types', (() => {
    const types = new Registry<EdmType>();

    function resolveType(
      name: TypeName | null,
      namespace?: string | null,
    ): EdmType | null {
      if (!name) {
        return null;
      }

      if (hasPostfix(name, COLLECTION_TYPE_POSTFIX)) {
        return (resolveType(
          removePostfix(name, COLLECTION_TYPE_POSTFIX),
          namespace,
        ) as EdmType).collectionType;
      }

      if (isOneOfType(name)) {
        if (!oneOfTypes.has(name)) {
          const prefixLength = ONEOF_TYPE_PREFIX.length + 1;
          const typeNames = name.substr(prefixLength, name.length - prefixLength - 1).split(',');
          oneOfTypes.set(name, new OneOfType({ typeNames }));
        }

        return oneOfTypes.get(name) || null;
      }

      return types.resolve(name, namespace) as EdmType | null;
    }

    class Property {
      constructor({
        name,
        typeName,
        namespace,
      }: PropertyOptions) {
        defineConstProperty(this, 'name', name);
        defineConstProperty(this, 'typeName', typeName);
        defineProducedProperty(this, 'type', () => resolveType(typeName, namespace));
      }
    }
    interface Property extends EdmProperty {}

    class Parameter {
      constructor({
        name,
        typeName,
        namespace,
      }: ParameterOptions) {
        defineConstProperty(this, 'name', name);
        defineConstProperty(this, 'typeName', typeName);
        defineProducedProperty(this, 'type', () => resolveType(typeName, namespace));
      }
    }
    interface Parameter extends EdmParameter {}

    class Type {
      constructor({ name }: TypeOptions) {
        defineConstProperty(this, 'name', name);
        defineConstProperty(this, 'namespace', Registry.getNamespace(name));
        defineConstProperty(this, 'shortName', Registry.getShortName(name));
        types.register(this);
      }
    }
    interface Type extends EdmType {}

    class PrimitiveType extends Type {
      constructor({
        name,
        jsType,
      }: PrimitiveTypeOptions) {
        super({ name });
        defineConstProperty(this, 'jsType', jsType);
      }
    }
    interface PrimitiveType extends EdmPrimitiveType {}

    class OneOfType extends Type {
      constructor({ typeNames }: OneOfTypeOptions) {
        super({
          name: OneOfType.oneOfTypeName(typeNames),
        });

        defineConstProperty(this, 'typeNames', typeNames);
        defineConstProperty(
          this,
          'types',
          typeNames.map(typeName => resolveType(typeName, this.namespace)),
        );
      }

      static oneOfTypeName(typeNames: TypeName[]) {
        // We don't support nested one for now
        const { namespace } = this as typeof OneOfType & { namespace?: NamespaceName };
        const sortedNames = typeNames
          .map(name => types.resolveQualifiedName(name, namespace))
          .sort();

        return `${ONEOF_TYPE_PREFIX}(${sortedNames.join(',')})`;
      }
    }
    interface OneOfType extends EdmType {
      readonly typeNames: TypeName[];
      readonly types: Array<EdmType | null>;
    }

    function compileProperties(
      this: EdmObjectType,
      properties: Record<string, PropertyInfo>,
    ) {
      const { namespace } = this;

      return _.mapObject(properties, ({ typeName }, name) => new Property({
        name,
        typeName,
        namespace,
      }));
    }

    class ObjectType extends Type {
      constructor({
        name,
        properties,
        navigationPropertyNames = [],
        baseTypeName,
      }: ObjectTypeOptions) {
        super({ name });

        defineConstProperty(this, 'properties', compileProperties.call(this, properties));
        defineConstProperty(this, 'navigationPropertyNames', navigationPropertyNames.slice());
        defineProducedProperty(
          this,
          'navigationProperties',
          () => _.pick(
            this.properties,
            (_property, propertyName) => _.contains(
              this.navigationPropertyNames,
              propertyName,
            ),
          ),
        );
        if (baseTypeName) {
          defineConstProperty(this, 'baseTypeName', baseTypeName);
          defineProducedProperty(
            this,
            'baseType',
            () => resolveType(this.baseTypeName, this.namespace) as EdmObjectType,
          );
        }
      }

      addProperties(properties: Record<string, PropertyInfo>) {
        _.extend(this.properties, compileProperties.call(this, properties));
        // only for backward compability, should use addNavigationProperties for this case
        this.navigationPropertyNames.push(..._.keys(properties));
      }

      addNavigationProperties(properties: Record<string, PropertyInfo>) {
        this.addProperties(properties);
      }
    }
    interface ObjectType extends EdmObjectType {}

    class ComplexType extends ObjectType {
    }

    class EntityType extends ObjectType {
      constructor({
        name,
        properties,
        navigationPropertyNames,
        baseTypeName,
        key,
      }: EntityTypeOptions) {
        super({
          name,
          properties,
          navigationPropertyNames,
          baseTypeName,
        });

        if (key) {
          defineConstProperty(this, 'key', key);
          defineConstProperty(this, 'keyProperty', this.properties[this.key]);
        } else if (baseTypeName) {
          // The key property is inherited if there's a base type
          defineProducedProperty(this, 'key', () => this.baseType.key);
          defineProducedProperty(this, 'keyProperty', () => this.baseType.keyProperty);
        } else {
          throw new Error('The "key" property is required for an EntityType');
        }
      }
    }
    interface EntityType extends EdmEntityType {
      readonly baseType: EdmEntityType;
    }

    class CollectionType extends ObjectType {
      constructor({
        properties = {},
        baseTypeName,
        elementTypeName,
      }: CollectionTypeOptions) {
        super({
          name: CollectionType.collectionTypeName(elementTypeName),
          properties,
          baseTypeName,
        });
        defineConstProperty(this, 'elementTypeName', elementTypeName);
        defineProducedProperty(
          this,
          'elementType',
          () => resolveType(this.elementTypeName, this.namespace) as EdmType,
        );
      }

      static collectionTypeName(typeName: TypeName | null) {
        return `${typeName}${COLLECTION_TYPE_POSTFIX}`;
      }
    }
    interface CollectionType extends EdmCollectionType {}

    function collectionTypeFactory(this: EdmType) {
      return new CollectionType({ elementTypeName: this.name });
    }

    defineProducedPropertyOnClass(Type, 'collectionType', collectionTypeFactory);

    function compileParameters(
      this: EdmCallableType,
      parameters: Record<string, ParameterInfo>,
    ) {
      const { namespace } = this;

      return _.mapObject(
        parameters,
        ({ typeName }, name) => new Parameter({ name, namespace, typeName }),
      );
    }

    class CallableType extends Type {
      constructor({
        name,
        callableName,
        parameters,
        returnTypeName,
      }: CallableTypeOptions) {
        super({ name });

        defineConstProperty(this, 'callableName', callableName);
        defineConstProperty(this, 'parameters', compileParameters.call(this, parameters));
        defineConstProperty(this, 'returnTypeName', returnTypeName);
        defineProducedProperty(
          this,
          'returnType',
          () => resolveType(returnTypeName, this.namespace),
        );
      }
    }
    interface CallableType extends EdmCallableType {}

    class ActionType extends CallableType {
    }

    class FunctionType extends CallableType {
    }

    defineConstProperty(types, 'Property', Property);
    defineConstProperty(types, 'Parameter', Parameter);
    defineConstProperty(types, 'Type', Type);
    defineConstProperty(types, 'PrimitiveType', PrimitiveType);
    defineConstProperty(types, 'OneOfType', OneOfType);
    defineConstProperty(types, 'ObjectType', ObjectType);
    defineConstProperty(types, 'ComplexType', ComplexType);
    defineConstProperty(types, 'EntityType', EntityType);
    defineConstProperty(types, 'CollectionType', CollectionType);
    defineConstProperty(types, 'CallableType', CallableType);
    defineConstProperty(types, 'ActionType', ActionType);
    defineConstProperty(types, 'FunctionType', FunctionType);

    return types as unknown as TypeRegistry;
  })());
}
